import type { CancelToken } from "axios";
import { ApiAuth, ApiCall } from "./api";
import { TPremiseResponse } from "./interface/IDefaults";
import {
  IFormData,
  ILoginResponse,
  ITwoFactorResponse,
  IFormRequestInput,
  IRequestResponse,
  IFormValidateOtpInput,
  IResetPasswordInput,
  IResetPasswordResponse,
  IWaitingValidation,
  IFormSSOInput,
  IChangePasswordResponse,
  IChangePasswordInput,
  ISsoReloginUrl,
} from "./interface/login.interface";
import { IResponse } from "./interface/user.interface";

export const getAuthValidate = async (): Promise<TPremiseResponse<IResponse>> => {
  return await new ApiCall("/auth/validate", ApiAuth()).get<IResponse>();
};

export const postLogin = async ({
  email,
  password,
  deviceCode,
}: IFormData): Promise<TPremiseResponse<ILoginResponse | ITwoFactorResponse | IWaitingValidation>> => {
  return await new ApiCall("/auth/login").post<ILoginResponse | ITwoFactorResponse, IFormData>({
    email,
    password,
    deviceCode,
  });
};

// Sem config, esta chamada herdava o timeout de 300s da instância axios
// (`caller()` em api.ts) — com `loginPending` travado até a troca do token, o botão
// Microsoft ficava desabilitado por até 5 minutos numa rede ruim. 30s é generoso para uma troca de
// token e já é o padrão usado em outras mutações do produto (ver `REQUEST_TIMEOUT_MS` do painel do
// portal). Um `AbortController` explícito seria redundante aqui: `config.timeout` do axios já aborta
// a requisição em voo no limite — não precisa de um segundo mecanismo para o mesmo efeito.
export const postLoginSSO = async ({
  email,
  name,
  clientSideToken,
  idToken,
  nonce,
}: IFormSSOInput): Promise<TPremiseResponse<ILoginResponse>> => {
  return await new ApiCall("/auth/login/sso", { timeout: 30000 }).post<ILoginResponse, IFormSSOInput>({
    email,
    name,
    clientSideToken,
    ...(idToken ? { idToken } : {}),
    ...(nonce ? { nonce } : {}),
  });
};

export const postRecoveryPass = async ({ email }: IFormRequestInput): Promise<TPremiseResponse<IRequestResponse>> => {
  return await new ApiCall("/auth/recover").post<IRequestResponse, IFormRequestInput>({ email });
};

export const postCheckOtp = async (data: IFormValidateOtpInput): Promise<TPremiseResponse<boolean>> => {
  return await new ApiCall("/auth/otp").post<boolean, IFormValidateOtpInput>(data);
};

export const postResetPassword = async (
  data: IResetPasswordInput
): Promise<TPremiseResponse<IResetPasswordResponse>> => {
  return await new ApiCall("/auth/reset-password").post<IResetPasswordResponse, IResetPasswordInput>(data);
};

export const putChangePassword = async (
  data: IChangePasswordInput
): Promise<TPremiseResponse<IChangePasswordResponse>> => {
  return await new ApiCall("/auth/change-password", ApiAuth()).put<IChangePasswordResponse, IChangePasswordInput>(data);
};

export const getIsSSO = async (): Promise<TPremiseResponse<boolean>> => {
  return await new ApiCall("/auth/is-sso").get<boolean>();
};

/**
 * GEN-4150 — URL de entrada do broker de SSO para renovar uma sessão expirada. Pública (a sessão
 * acabou de morrer); 404 quando a chave não está na allowlist do backend.
 */
export const getSsoReloginUrl = async (
  key: string,
  cancelToken?: CancelToken
): Promise<TPremiseResponse<ISsoReloginUrl>> => {
  return await new ApiCall(
    `/auth/sso-relogin/${encodeURIComponent(key)}`,
    undefined,
    cancelToken
  ).get<ISsoReloginUrl>();
};
